/**
 * Foreign-Vendor-Pattern-Verwaltung (Admin).
 *
 * Feb 2026 — ersetzt das hardcoded `KNOWN_FOREIGN_VENDORS`-Set durch eine
 * DB-Tabelle, die der Admin bearbeiten kann.
 *
 * Endpoints:
 *   GET    /api/admin/foreign-vendors
 *   POST   /api/admin/foreign-vendors
 *   PATCH  /api/admin/foreign-vendors/:id
 *   DELETE /api/admin/foreign-vendors/:id
 *   POST   /api/admin/foreign-vendors/seed
 */
import React, { useEffect, useState } from 'react';
import api from '@/utils/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus, Trash2, RefreshCw, Save, X } from 'lucide-react';

const SCENARIO_LABELS = {
  reverse_charge: 'Reverse Charge',
  standard: 'Standard (mit USt)',
};

export default function ForeignVendorsAdminPanel() {
  const [patterns, setPatterns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('');
  const [newRow, setNewRow] = useState({ pattern: '', country: '', notes: '' });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/foreign-vendors');
      setPatterns(res.data.patterns || []);
    } catch (e) {
      toast.error('Fehler beim Laden: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleSeed = async () => {
    if (!window.confirm('Initial-Patterns (~30 Stück) anlegen? Idempotent — überspringt vorhandene.')) return;
    try {
      const res = await api.post('/admin/foreign-vendors/seed');
      toast.success(`${res.data.inserted} neue Pattern(s) angelegt`);
      await load();
    } catch (e) {
      toast.error('Seed fehlgeschlagen: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const handleAdd = async () => {
    if (!newRow.pattern.trim() || !newRow.country.trim()) {
      toast.error('Pattern und Country sind Pflicht.');
      return;
    }
    try {
      await api.post('/admin/foreign-vendors', {
        pattern: newRow.pattern.toLowerCase().trim(),
        country: newRow.country.toUpperCase().trim(),
        default_tax_scenario: 'reverse_charge',
        notes: newRow.notes.trim() || undefined,
      });
      toast.success('Pattern angelegt');
      setNewRow({ pattern: '', country: '', notes: '' });
      await load();
    } catch (e) {
      toast.error('Fehler: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const handleSaveEdit = async (id) => {
    try {
      await api.patch(`/admin/foreign-vendors/${id}`, {
        pattern: editForm.pattern?.toLowerCase().trim(),
        country: editForm.country?.toUpperCase().trim(),
        default_tax_scenario: editForm.default_tax_scenario,
        notes: editForm.notes?.trim() || null,
      });
      toast.success('Aktualisiert');
      setEditingId(null);
      await load();
    } catch (e) {
      toast.error('Fehler: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const handleDelete = async (id, pattern) => {
    if (!window.confirm(`Pattern "${pattern}" löschen?`)) return;
    try {
      await api.delete(`/admin/foreign-vendors/${id}`);
      toast.success('Gelöscht');
      await load();
    } catch (e) {
      toast.error('Fehler: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const filtered = patterns.filter(p =>
    !filter || p.pattern.includes(filter.toLowerCase()) || p.country.includes(filter.toUpperCase()) || (p.notes || '').toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div className="space-y-6" data-testid="foreign-vendors-admin-panel">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">🌍 Foreign-Vendor-Patterns</h2>
          <p className="text-sm text-slate-600 mt-1 max-w-3xl">
            Diese Pattern werden für die automatische Reverse-Charge-Erkennung verwendet. Wenn ein Beleg keine USt zeigt und der Vendor-Name einen dieser Patterns enthält, wird das Land aus dieser Tabelle übernommen — und der Beleg automatisch als RC-Verdacht geflaggt.
          </p>
        </div>
        <div className="flex gap-2">
          <Button onClick={handleSeed} variant="outline" data-testid="seed-foreign-vendors-btn">
            <RefreshCw className="w-4 h-4 mr-1" /> Initial-Seed
          </Button>
          <Button onClick={load} variant="outline">
            <RefreshCw className="w-4 h-4 mr-1" /> Neu laden
          </Button>
        </div>
      </div>

      {/* Add New Row */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Neues Pattern</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-2 items-end">
            <div>
              <Label className="text-xs">Pattern (Substring, lowercase)</Label>
              <Input
                value={newRow.pattern}
                onChange={(e) => setNewRow({ ...newRow, pattern: e.target.value })}
                placeholder="z.B. github"
                data-testid="new-pattern-input"
              />
            </div>
            <div>
              <Label className="text-xs">Land (ISO-2)</Label>
              <Input
                value={newRow.country}
                onChange={(e) => setNewRow({ ...newRow, country: e.target.value.toUpperCase().slice(0, 2) })}
                placeholder="US"
                maxLength={2}
                data-testid="new-country-input"
              />
            </div>
            <div className="md:col-span-1">
              <Label className="text-xs">Notiz</Label>
              <Input
                value={newRow.notes}
                onChange={(e) => setNewRow({ ...newRow, notes: e.target.value })}
                placeholder="z.B. AI-API"
                data-testid="new-notes-input"
              />
            </div>
            <Button onClick={handleAdd} data-testid="add-pattern-btn">
              <Plus className="w-4 h-4 mr-1" /> Hinzufügen
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-base">{patterns.length} Pattern(s) gesamt</CardTitle>
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Suche…"
              className="max-w-xs"
              data-testid="filter-patterns-input"
            />
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-sm text-slate-500">Lade…</div>
          ) : filtered.length === 0 ? (
            <div className="text-sm text-slate-500">Keine Patterns. Initial-Seed durchführen?</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs text-slate-500 border-b">
                  <tr>
                    <th className="text-left py-2 px-2">Pattern</th>
                    <th className="text-left py-2 px-2">Land</th>
                    <th className="text-left py-2 px-2">Szenario</th>
                    <th className="text-left py-2 px-2">Notiz</th>
                    <th className="text-right py-2 px-2">Aktionen</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((p) => (
                    <tr key={p.id} className="border-b hover:bg-slate-50" data-testid={`pattern-row-${p.pattern}`}>
                      {editingId === p.id ? (
                        <>
                          <td className="py-2 px-2">
                            <Input value={editForm.pattern} onChange={(e) => setEditForm({ ...editForm, pattern: e.target.value })} className="h-8" />
                          </td>
                          <td className="py-2 px-2">
                            <Input value={editForm.country} onChange={(e) => setEditForm({ ...editForm, country: e.target.value.toUpperCase().slice(0, 2) })} className="h-8 w-16" />
                          </td>
                          <td className="py-2 px-2">
                            <select
                              value={editForm.default_tax_scenario || 'reverse_charge'}
                              onChange={(e) => setEditForm({ ...editForm, default_tax_scenario: e.target.value })}
                              className="h-8 border rounded px-2 text-sm"
                            >
                              <option value="reverse_charge">Reverse Charge</option>
                              <option value="standard">Standard</option>
                            </select>
                          </td>
                          <td className="py-2 px-2">
                            <Input value={editForm.notes || ''} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} className="h-8" />
                          </td>
                          <td className="py-2 px-2 text-right">
                            <Button size="sm" variant="ghost" onClick={() => handleSaveEdit(p.id)} data-testid={`save-pattern-${p.pattern}`}>
                              <Save className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>
                              <X className="w-4 h-4" />
                            </Button>
                          </td>
                        </>
                      ) : (
                        <>
                          <td className="py-2 px-2 font-mono text-xs">{p.pattern}</td>
                          <td className="py-2 px-2"><span className="inline-block bg-blue-100 text-blue-800 rounded px-2 py-0.5 text-xs font-semibold">{p.country}</span></td>
                          <td className="py-2 px-2 text-xs text-slate-600">{SCENARIO_LABELS[p.default_tax_scenario] || p.default_tax_scenario}</td>
                          <td className="py-2 px-2 text-xs text-slate-600">{p.notes || '—'}</td>
                          <td className="py-2 px-2 text-right">
                            <Button size="sm" variant="ghost" onClick={() => { setEditingId(p.id); setEditForm(p); }} data-testid={`edit-pattern-${p.pattern}`}>
                              Bearbeiten
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleDelete(p.id, p.pattern)} className="text-red-600" data-testid={`delete-pattern-${p.pattern}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
